import { useState, useEffect, useCallback, useMemo } from 'react';
import metricsService from '../services/metrics.service';

/**
 * Hook for fetching and managing metrics data
 *
 * @param {string} storeId - Store ID or 'all' for all stores
 * @param {string} initialRange - Initial date range preset ('7days' or '30days')
 * @returns {Object} Metrics state and controls
 */
export function useMetrics(storeId = 'all', initialRange = '7days') {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [previousRange, setPreviousRange] = useState(null);
  const [tickInterval, setTickInterval] = useState(0);
  const [dateRange, setDateRange] = useState(initialRange);
  const [customRange, setCustomRange] = useState(null);
  const [dayOfWeekData, setDayOfWeekData] = useState([]);
  const [productMix, setProductMix] = useState([]);
  const [channelMix, setChannelMix] = useState([]);

  // Memoize presets to prevent infinite re-render loop
  const presets = useMemo(() => metricsService.getDateRangePresets(), []);

  /**
   * Fetch metrics for the current date range and store
   */
  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      let startDate, endDate;

      if (customRange) {
        startDate = customRange.startDate;
        endDate = customRange.endDate;
      } else {
        const preset = presets[dateRange];
        startDate = preset.startDate;
        endDate = preset.endDate;
      }

      // Skip comparison for "all" time range and 1 year (doesn't make sense to compare)
      // Also skip for very long periods (over 1 year)
      const start = new Date(startDate);
      const end = new Date(endDate);
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
      const skipComparison = dateRange === 'all' || dateRange === '1year' || daysDiff > 365;

      let result;
      if (skipComparison) {
        // Just fetch current period, no comparison
        const metrics = await metricsService.fetchMetrics(startDate, endDate, storeId);
        const transformed = metricsService.transformForCharts(metrics);
        result = {
          current: transformed,
          previous: { summary: null },
          previousRange: null,
        };
      } else {
        // Fetch current period with year-over-year comparison (same dates last year)
        const yoyResult = await metricsService.fetchMetricsWithYoY(
          startDate,
          endDate,
          storeId
        );
        const currentData = metricsService.transformForCharts(yoyResult.current);
        const previousData = metricsService.transformForCharts(yoyResult.lastYear);
        result = {
          current: currentData,
          previous: previousData,
          previousRange: yoyResult.lastYearRange,
        };
      }

      setChartData(result.current.chartData);
      setSummary(result.current.summary);
      setTickInterval(result.current.tickInterval);
      setPreviousSummary(result.previous.summary);
      setPreviousRange(result.previousRange);

      // Calculate day-of-week averages
      const dowData = metricsService.calculateDayOfWeekAverages(result.current.chartData);
      setDayOfWeekData(dowData);

      // Calculate product mix
      const mix = metricsService.calculateProductMix(result.current.summary);
      setProductMix(mix);

      // Calculate channel mix
      const channels = metricsService.calculateChannelMix(result.current.summary);
      setChannelMix(channels);
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setError(err.message || 'Error al cargar métricas');
      setChartData([]);
      setSummary(null);
      setPreviousSummary(null);
      setPreviousRange(null);
      setTickInterval(0);
      setDayOfWeekData([]);
      setProductMix([]);
      setChannelMix([]);
    } finally {
      setLoading(false);
    }
  }, [storeId, dateRange, customRange, presets]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Change to a preset date range
   */
  const selectPreset = useCallback((presetKey) => {
    setCustomRange(null);
    setDateRange(presetKey);
  }, []);

  /**
   * Set a custom date range
   */
  const selectCustomRange = useCallback((startDate, endDate) => {
    setCustomRange({ startDate, endDate });
    setDateRange('custom');
  }, []);

  /**
   * Refresh metrics data
   */
  const refresh = useCallback(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  /**
   * Get current date range for display
   */
  const getCurrentDateRange = useCallback(() => {
    if (customRange) {
      return {
        label: 'Personalizado',
        startDate: customRange.startDate,
        endDate: customRange.endDate,
      };
    }
    return presets[dateRange];
  }, [customRange, dateRange, presets]);

  /**
   * Format date range label for comparison display
   */
  const formatRangeLabel = useCallback((startDate, endDate) => {
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const formatOpts = { day: 'numeric', month: 'short' };
    return `${start.toLocaleDateString('es-MX', formatOpts)} - ${end.toLocaleDateString('es-MX', formatOpts)}`;
  }, []);

  /**
   * Get comparison labels based on current date range
   */
  const getComparisonLabels = useCallback(() => {
    const current = getCurrentDateRange();
    if (!previousRange || !current) {
      return { currentLabel: '', previousLabel: '' };
    }

    return {
      currentLabel: formatRangeLabel(current.startDate, current.endDate),
      previousLabel: formatRangeLabel(previousRange.startDate, previousRange.endDate),
    };
  }, [getCurrentDateRange, previousRange, formatRangeLabel]);

  return {
    // Data
    loading,
    error,
    chartData,
    summary,
    previousSummary,
    tickInterval,
    dayOfWeekData,
    productMix,
    channelMix,
    // Comparison labels
    comparisonLabels: getComparisonLabels(),
    // Date range state
    dateRange,
    customRange,
    presets,
    currentDateRange: getCurrentDateRange(),
    // Actions
    selectPreset,
    selectCustomRange,
    refresh,
  };
}

export default useMetrics;
